/**
 * Aureus Social Pro — Moteur Intérim CP 322 (Agence d'intérim)
 * ============================================================
 *
 * Calcul de paie pour les TRAVAILLEURS INTÉRIMAIRES employés par une
 * AGENCE D'INTÉRIM agréée (CP 322 — Travail intérimaire).
 *
 * À NE PAS CONFONDRE avec le case 'interimaire' du payroll-engine, qui
 * lui est conçu pour les CLIENTS qui EMBAUCHENT un intérimaire via une
 * agence externe (perspective utilisateur).
 *
 * Ici on est dans la perspective AGENCE : on emploie le travailleur
 * intérimaire, on lui verse le salaire, on déclare ONSS, on lui paie
 * la prime de fin d'année et le double pécule, etc.
 *
 * Source des constantes : app/lib/lois-belges.js (cotisations.interimCP322)
 * Sources légales : CCT CP 322 + ONSS 2026
 *
 * Règles couvertes :
 *  - Cotisations ONSS standard (13,07% travailleur / 25,07% patronal)
 *  - Prime de fin d'année CP 322 (8,33% du brut période 1/7-30/6 — Fonds Social)
 *  - Double pécule de vacances (92% du brut mensuel, versé annuellement)
 *  - Cotisation Fonds Social Intérimaires (0,10% patronal)
 *  - Précompte professionnel standard (formule-clé SPF 2026)
 *
 * NON couvert dans ce module (scope ultérieur) :
 *  - Génération Dimona OTH (module Dimona)
 *  - DmfA codes travailleurs intérim (module DmfA)
 *  - Indemnité de fidélité (en cours de définition par la CCT 2026)
 */

import { LB, TX_ONSS_W, TX_ONSS_E, PRIME_FIN_ANNEE_INTERIM } from './lois-belges'
import { calcPrecompteExact } from '@/app/lib/payroll'

const r2 = (n) => Math.round(n * 100) / 100

/**
 * Calcule le salaire complet d'un intérimaire CP 322 (côté agence).
 *
 * @param {Object} params
 * @param {number} params.brutMensuel - Salaire brut mensuel (€)
 * @param {number} [params.heures] - Heures prestées sur la période (info)
 * @param {number} [params.anciennete] - Ancienneté en mois (pour fidélité)
 * @param {Object} [params.precompteContext] - Context PP (enfants, conjoint, etc.)
 * @returns {Object} Détail complet du calcul
 */
export function calcSalaireInterimCP322({
  brutMensuel,
  heures = 0,
  anciennete = 0,
  precompteContext = {},
} = {}) {
  if (typeof brutMensuel !== 'number' || brutMensuel < 0) {
    throw new Error('calcSalaireInterimCP322: brutMensuel invalide')
  }

  const C = LB?.cotisations?.interimCP322 || {
    primeFinAnnee: PRIME_FIN_ANNEE_INTERIM,
    doublePeculeRatio: 0.92,
    fondsSocialPatronal: 0.001,
    onssTravailleur: TX_ONSS_W,
    onssPatronal: TX_ONSS_E,
  }

  const brutR = r2(brutMensuel)

  // Cotisations ONSS standard
  const onssW = r2(brutR * (C.onssTravailleur ?? TX_ONSS_W))
  const onssE = r2(brutR * (C.onssPatronal ?? TX_ONSS_E))

  // Cotisation Fonds Social Intérimaires (patronal uniquement)
  const fondsSocial = r2(brutR * (C.fondsSocialPatronal ?? 0.001))

  // Brut imposable et précompte
  const brutImp = r2(brutR - onssW)
  const pp = r2(calcPrecompteExact(brutImp, precompteContext).pp)

  // Cotisation spéciale sécurité sociale (CSSS)
  // Formule simplifiée — voir payroll-engine pour la formule trimestrielle exacte
  const css = brutImp > 1945.38 ? r2((brutImp - 1945.38) * 0.011) : 0

  const net = r2(brutImp - pp - css)

  // Pour info : provisions annuelles
  const provisionPrimeFinAnnee = r2(brutR * (C.primeFinAnnee ?? PRIME_FIN_ANNEE_INTERIM)) // 8,33% — CP 322 intérim (fallback aligné lois-belges)
  const provisionDoublePecule = r2(brutR * (C.doublePeculeRatio ?? 0.92) / 12)

  // Coût employeur total (mensuel, hors provisions annualisées)
  const coutTotal = r2(brutR + onssE + fondsSocial)

  // Coût annuel projeté (avec primes annuelles réparties)
  const coutAnnuelProjete = r2(
    coutTotal * 12 +
    provisionPrimeFinAnnee +
    (provisionDoublePecule * 12)
  )

  return {
    statut: 'interimaire_agence',
    cp: '322.00',
    brutR,
    heures,
    anciennete,
    onssW,
    onssE,
    fondsSocial,
    brutImp,
    pp,
    css,
    net,
    coutTotal,
    coutAnnuelProjete,
    provisionPrimeFinAnnee,
    provisionDoublePecule,
    fiche: '281.10',
    label: 'Intérimaire (agence CP 322)',
    note: 'Calcul agence d\'intérim — prime fin d\'année 5,76% + double pécule 92% provisionnés annuellement',
  }
}

/**
 * Calcule la prime de fin d'année CP 322 sur la base d'un brut annuel.
 * Versée par le Fonds Social pour la sécurité d'existence des intérimaires.
 *
 * @param {number} brutAnnuel - Brut total cumulé sur l'année
 * @returns {{ prime: number, taux: number, base: number }}
 */
export function calcPrimeFinAnneeCP322(brutAnnuel) {
  if (typeof brutAnnuel !== 'number' || brutAnnuel < 0) {
    throw new Error('calcPrimeFinAnneeCP322: brutAnnuel invalide')
  }
  const taux = LB?.cotisations?.interimCP322?.primeFinAnnee ?? PRIME_FIN_ANNEE_INTERIM
  return {
    prime: r2(brutAnnuel * taux),
    taux,
    base: r2(brutAnnuel),
    note: 'Prime de fin d\'année CP 322 — 8,33% du brut annuel (Fonds Social Intérimaires, période 1/7-30/6, min 65j ou 494h)',
  }
}

/**
 * Calcule le double pécule de vacances pour un intérimaire CP 322.
 * Versé en mai/juin sur la base du brut de l'exercice de vacances précédent.
 *
 * @param {number} brutExerciceVacances - Brut de l'année N-1
 * @returns {{ doublePecule: number, ratio: number, base: number }}
 */
export function calcDoublePeculeCP322(brutExerciceVacances) {
  if (typeof brutExerciceVacances !== 'number' || brutExerciceVacances < 0) {
    throw new Error('calcDoublePeculeCP322: brutExerciceVacances invalide')
  }
  const ratio = LB?.cotisations?.interimCP322?.doublePeculeRatio ?? 0.92
  return {
    doublePecule: r2(brutExerciceVacances * ratio),
    ratio,
    base: r2(brutExerciceVacances),
    note: 'Double pécule de vacances CP 322 — 92% du brut exercice précédent (versé mai/juin)',
  }
}

export default {
  calcSalaireInterimCP322,
  calcPrimeFinAnneeCP322,
  calcDoublePeculeCP322,
}
